import React from 'react';
import { Layout, Menu, Modal, Typography, Button, Grid, Drawer } from 'antd';
import { HomeOutlined, ToolOutlined, ApartmentOutlined, DollarOutlined, UserOutlined, LogoutOutlined, PlusOutlined, QuestionCircleOutlined, FileTextOutlined, PictureOutlined, BgColorsOutlined, MailOutlined, ClockCircleOutlined, CalculatorOutlined, BankOutlined, LineChartOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import Header from './Header';
import useSessionTimeout from '../hooks/useSessionTimeout';
import { logoutUser } from '../apiCalls';
import { clearStoredUser, getStoredUser } from '../utils/safeLocalStorage';
import { isAdminLike, isSuperAdmin } from '../utils/roleUtils';

const PageLayout = ({
    children,
    companyName,
    chooseProperty,
    propList,
    activePropertyName,
    property,
    role,
    signOut,
    confirmIfDirty,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const isLoggedIn = !!getStoredUser();

    const screens = Grid.useBreakpoint();
    const isMobile = screens.md === false;
    const [drawerOpen, setDrawerOpen] = React.useState(false);

    const handleSessionExpiry = async () => {
        try { await logoutUser(); } catch { /* ignore */ }
        clearStoredUser();
        localStorage.removeItem('selectedProperty');
        navigate('/login');
    };

    const { showWarning, secondsLeft, stayLoggedIn } = useSessionTimeout({
        onLogout: handleSessionExpiry,
        enabled: isLoggedIn,
    });

    const getActiveKey = () => {
        if (pathname.startsWith('/variables')) return 'variables';
        if (pathname.startsWith('/rules')) return 'rules';
        if (pathname.startsWith('/custom-costs')) return 'custom-costs';
        if (pathname.startsWith('/pdf-content')) return 'pdf-content';
        if (pathname.startsWith('/cost-table')) return 'cost-table';
        if (pathname.startsWith('/pdf-layout')) return 'pdf-layout';
        if (pathname.startsWith('/pdf-designer')) return 'pdf-designer';
        if (pathname.startsWith('/email-config')) return 'email-config';
        if (pathname.startsWith('/images')) return 'images';
        if (pathname.startsWith('/create-user')) return 'add-user';
        if (pathname.startsWith('/calculator-preview')) return 'calculator-preview';
        if (pathname.startsWith('/account-settings')) return 'account-settings';
        if (pathname.startsWith('/guide')) return 'guide';
        // Super Admin hub and all pages reachable from it share one active key
        if (
            pathname.startsWith('/super-admin') ||
            pathname.startsWith('/outreach-monitor') ||
            pathname.startsWith('/audit-log') ||
            pathname.startsWith('/dashboard') ||
            pathname.startsWith('/journey-funnel') ||
            pathname.startsWith('/leads') ||
            pathname.startsWith('/website-demos') ||
            pathname.startsWith('/property-manager') ||
            pathname.startsWith('/customer-onboarding')
        ) return 'super-admin';
        return 'home';
    };

    const activeKey = getActiveKey();

    const closeDrawer = () => setDrawerOpen(false);

    const menuContent = (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ flex: 1 }}>
                <Menu
                    mode="inline"
                    selectedKeys={[activeKey]}
                    style={{ borderRight: 0, paddingTop: 16 }}
                    onClick={closeDrawer}
                    items={[
                        {
                            key: 'home',
                            icon: <HomeOutlined />,
                            label: t('nav.home'),
                            onClick: () => navigate('/'),
                        },
                        ...(isAdminLike(role) ? [
                            {
                                key: 'journey-funnel',
                                icon: <LineChartOutlined />,
                                label: 'Calculator journey',
                                onClick: () => navigate('/journey-funnel'),
                            },
                            {
                                key: 'calculator-preview',
                                icon: <CalculatorOutlined />,
                                label: t('nav.calculatorPreview'),
                                disabled: !property,
                                onClick: () => navigate(`/calculator-preview/${property}`),
                            },
                            {
                                type: 'group',
                                label: t('nav.configuration'),
                                children: [
                                    {
                                        key: 'variables',
                                        icon: <ToolOutlined />,
                                        label: t('nav.costVariables'),
                                        disabled: !property,
                                        onClick: () => navigate(`/variables/${property}`),
                                    },
                                    {
                                        key: 'rules',
                                        icon: <ApartmentOutlined />,
                                        label: t('nav.calculationRules'),
                                        disabled: !property,
                                        onClick: () => navigate(`/rules/${property}`),
                                    },
                                    {
                                        key: 'custom-costs',
                                        icon: <DollarOutlined />,
                                        label: t('nav.customCosts'),
                                        disabled: !property,
                                        onClick: () => navigate(`/custom-costs/${property}`),
                                    },
                                    {
                                        key: 'pdf-content',
                                        icon: <FileTextOutlined />,
                                        label: t('nav.pdfSettings'),
                                        disabled: !property,
                                        onClick: () => navigate(`/pdf-content/${property}`),
                                    },
                                    {
                                        key: 'pdf-layout',
                                        icon: <BgColorsOutlined />,
                                        label: t('nav.pdfLayout'),
                                        disabled: !property,
                                        onClick: () => navigate(`/pdf-layout/${property}`),
                                    },
                                    {
                                        key: 'pdf-designer',
                                        icon: <ApartmentOutlined />,
                                        label: t('nav.pdfPageDesigner'),
                                        disabled: !property,
                                        onClick: () => navigate(`/pdf-designer/${property}`),
                                    },
                                    {
                                        key: 'email-config',
                                        icon: <MailOutlined />,
                                        label: t('nav.emailConfiguration'),
                                        disabled: !property,
                                        onClick: () => navigate(`/email-config/${property}`),
                                    },
                                    {
                                        key: 'images',
                                        icon: <PictureOutlined />,
                                        label: t('nav.imageManagement'),
                                        disabled: !property,
                                        onClick: () => confirmIfDirty(() => navigate(`/images/${property}`)),
                                    },
                                    {
                                        key: 'add-user',
                                        icon: <PlusOutlined />,
                                        label: t('nav.addNewUser'),
                                        onClick: () => navigate('/create-user'),
                                    },
                                ],
                            },
                            ...(isSuperAdmin(role) ? [
                                { type: 'divider' },
                                {
                                    key: 'super-admin',
                                    icon: <BankOutlined />,
                                    label: 'Super Admin',
                                    onClick: () => navigate('/super-admin'),
                                },
                            ] : []),
                        ] : []),
                    ]}
                />
            </div>
            <div>
                <Menu
                    mode="inline"
                    selectedKeys={[activeKey]}
                    style={{ borderRight: 0, borderTop: '1px solid #f0f0f0' }}
                    onClick={closeDrawer}
                    items={[
                        ...(isAdminLike(role) ? [
                            {
                                key: 'guide',
                                icon: <QuestionCircleOutlined />,
                                label: t('nav.howToGuide'),
                                onClick: () => confirmIfDirty(() => navigate('/guide')),
                            },
                        ] : []),
                        {
                            key: 'account-settings',
                            icon: <UserOutlined />,
                            label: t('nav.accountSettings'),
                            onClick: () => confirmIfDirty(() => navigate('/account-settings')),
                        },
                        {
                            key: 'sign-out',
                            icon: <LogoutOutlined />,
                            label: t('nav.signOut'),
                            onClick: signOut,
                        },
                    ]}
                />
            </div>
        </div>
    );

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Modal
                open={showWarning}
                footer={null}
                closable={false}
                centered
                title={<span><ClockCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />{t('session.title')}</span>}
            >
                <Typography.Paragraph>
                    <Trans i18nKey="session.message" values={{ seconds: secondsLeft }} components={{ strong: <strong /> }} />
                </Typography.Paragraph>
                <Button type="primary" block onClick={stayLoggedIn} style={{ marginBottom: 8 }}>
                    {t('session.staySignedIn')}
                </Button>
                <Button block danger onClick={handleSessionExpiry}>
                    {t('session.signOutNow')}
                </Button>
            </Modal>
            <Header
                name={companyName}
                chooseProperty={chooseProperty}
                propList={propList}
                activePropertyName={activePropertyName}
                role={role}
                isMobile={isMobile}
                onMenuClick={() => setDrawerOpen(true)}
            />
            <Layout>
                {isMobile ? (
                    <Drawer
                        placement="left"
                        open={drawerOpen}
                        onClose={closeDrawer}
                        width={220}
                        styles={{ body: { padding: 0 } }}
                        title={null}
                        closable={false}
                    >
                        {menuContent}
                    </Drawer>
                ) : (
                    <Layout.Sider
                        width={220}
                        style={{ background: '#fff', boxShadow: '2px 0 8px rgba(0,0,0,0.06)' }}
                    >
                        {menuContent}
                    </Layout.Sider>
                )}
                <Layout.Content style={{ padding: isMobile ? '16px 12px' : '32px 24px', background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)' }}>
                    {children}
                </Layout.Content>
            </Layout>
            <Layout.Footer style={{
                textAlign: 'center',
                background: '#fff',
                borderTop: '1px solid #f0f0f0',
                padding: '16px 24px',
                color: 'rgba(0, 0, 0, 0.65)',
                fontWeight: 500,
            }}>
                {t('footer.copyright', { name: companyName, year: new Date().getFullYear() })}
            </Layout.Footer>
        </Layout>
    );
};

export default PageLayout;
